import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "../features/i18n/useT";
import { usePageTag } from "../app/usePageTag";
import { useEvents } from "../features/calendar/useEvents";
import { getTodayLessons, getCurrentLesson, getNextLesson, getUpcomingDeadlines } from "../features/calendar/selectors";
import { formatTime } from "../shared/utils/date";
import { openExternalUrl } from "../shared/utils/safeUrl";
import { openMapWithRoom } from "../features/map/roomLink";
import { useDone } from "../features/done/useDone";
import { readReminderInbox, ReminderInboxItem, markReminderInboxItemRead } from "../features/notifications/reminderInbox";

function formatLessonType(type: string | undefined, t: (k: string) => string) {
  const raw = (type || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("lec") || raw.includes("лекц")) return t("schedule.lessonType.lecture");
  if (raw.includes("prac") || raw.includes("практ")) return t("schedule.lessonType.practice");
  return type || "";
}

function formatDuration(ms: number) {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function toDueMs(dueAt: number | string | undefined) {
  if (dueAt === null || dueAt === undefined) return null;
  const raw = typeof dueAt === "number" ? dueAt : Date.parse(dueAt);
  if (!Number.isFinite(raw)) return null;
  return raw < 1_000_000_000_000 ? raw * 1000 : raw;
}

function formatCountdown(dueAt: number | string | undefined, now: number) {
  const dueMs = toDueMs(dueAt);
  if (!dueMs) return "";
  const diff = dueMs - now;
  const dayMs = 24 * 60 * 60 * 1000;
  if (Math.abs(diff) > dayMs) return "";
  if (diff >= 0) return `in ${formatDuration(diff)}`;
  return `over ${formatDuration(-diff)}`;
}

export default function HomePage() {
  usePageTag("home");
  const t = useT();
  const events = useEvents();
  const { isDone } = useDone();

  const now = Date.now();
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inbox, setInbox] = useState<ReminderInboxItem[]>(() => readReminderInbox());
  const [inboxNow, setInboxNow] = useState(() => Date.now());
  const inboxBtnRef = useRef<HTMLButtonElement | null>(null);
  const inboxPanelRef = useRef<HTMLDivElement | null>(null);

  const refreshInbox = () => setInbox(readReminderInbox());
  const markInboxItemRead = (id: string) => {
    markReminderInboxItemRead(id);
    refreshInbox();
  };

  useEffect(() => {
    refreshInbox();
    const onUpdate = () => refreshInbox();
    window.addEventListener("reminder-inbox-updated", onUpdate);
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener("reminder-inbox-updated", onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, []);

  useEffect(() => {
    if (!inboxOpen) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (inboxPanelRef.current?.contains(target)) return;
      if (inboxBtnRef.current?.contains(target)) return;
      setInboxOpen(false);
    };
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [inboxOpen]);

  useEffect(() => {
    if (!inboxOpen) return;
    const id = window.setInterval(() => setInboxNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [inboxOpen]);

  const lessonsToday = useMemo(() => getTodayLessons(events), [events]);
  const currentLesson = useMemo(() => getCurrentLesson(events, now), [events, now]);
  const nextLesson = useMemo(() => getNextLesson(events, now), [events, now]);
  const upcomingDeadlines = useMemo(
    () => getUpcomingDeadlines(events, 4, now).filter((d) => !isDone(d.uid)),
    [events, now, isDone]
  );

  return (
    <div className="page">
      <div className="home-header">
        <h2>{t("home.title")}</h2>
        <div className="home-inbox">
          <button
            ref={inboxBtnRef}
            className="notif-btn"
            onClick={() => setInboxOpen((v) => !v)}
            aria-label={t("home.reminders")}
            title={t("home.reminders")}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 1 0-14 0v5l-2 2v1h18v-1l-2-2Z"
                fill="currentColor"
              />
            </svg>
            {inbox.some((i) => !i.readAt) ? (
              <span className="notif-badge">{inbox.filter((i) => !i.readAt).length}</span>
            ) : null}
          </button>

          {inboxOpen ? (
            <div ref={inboxPanelRef} className="notif-panel">
              <div className="notif-title">{t("home.reminders")}</div>
              {inbox.length === 0 ? (
                <div className="muted small">{t("home.reminders.empty")}</div>
              ) : (
                <div className="notif-list">
                  {inbox.map((item) => (
                    <div
                      key={item.id}
                      className={`notif-item${item.readAt ? " is-read" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => markInboxItemRead(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          markInboxItemRead(item.id);
                        }
                      }}
                    >
                      <div className="title">{item.title}</div>
                      <div className="muted small">{item.body}</div>
                      {(() => {
                        const countdown = formatCountdown(item.dueAt, inboxNow);
                        return countdown ? <div className="muted small">{countdown}</div> : null;
                      })()}
                      <div className="muted small">{new Date(item.at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="home-hero">
        <div className="hero-card accent-border">
          <div className="hero-title">{t("home.now")}</div>
          <div className="hero-sub">{currentLesson ? "" : t("home.noLessonsToday.sub")}</div>

          {currentLesson ? (
            <div className="list" style={{ marginTop: 10 }}>
              <div className="today-card accent-border">
                <div className="schedule-lesson-card compact">
                <div className="lesson-time">
                  <div className="time-range">
                    {formatTime(currentLesson.start)} - {currentLesson.end ? formatTime(currentLesson.end) : "--:--"}
                  </div>
                </div>

                  <div className="content">
                    <div className="title-row">
                      <div className="title">{currentLesson.title}</div>
                    </div>
                    
                    <div className="meta">
                      {currentLesson.lessonType ? <span className="pill pill-off">{formatLessonType(currentLesson.lessonType, t)}</span> : null}
                      {currentLesson.teacher ? <span className="pill pill-on">{currentLesson.teacher}</span> : null}
                      {currentLesson.room ? (
                        <button
                          className="pill pill-off"
                          onClick={() => {
                            if (currentLesson.room) openMapWithRoom(currentLesson.room);
                          }}
                          style={{ border: "none", background: "inherit", cursor: "pointer" }}
                        >
                          {currentLesson.room}
                        </button>
                      ) : (
                        <span className="pill pill-muted">{t("common.noRoom")}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="empty" style={{ marginTop: 10 }}>
              <div className="empty-title">{t("home.noCurrentLesson")}</div>
              <div className="muted small">{t("home.noNextLesson")}</div>
            </div>
          )}

          <div className="section">
            <h3>{t("home.today")}</h3>

            {lessonsToday.length === 0 ? (
              <div className="empty">
                <div className="empty-title">{t("home.noLessonsToday.title")}</div>
                <div className="muted small">{t("home.noLessonsToday.sub")}</div>
              </div>
            ) : (
              <div className="list">
                {lessonsToday.map((e) => (
                  <div key={e.uid} className="today-card accent-border">
                    <div className="schedule-lesson-card compact">
                      <div className="lesson-time">
                        <div className="time-range">
                          {formatTime(e.start)} - {e.end ? formatTime(e.end) : "--:--"}
                        </div>
                      </div>

                      <div className="content">
                        <div className="title-row">
                          <div className="title">{e.title}</div>
                        </div>
                        
                        <div className="meta">
                          {e.lessonType ? <span className="pill pill-off">{formatLessonType(e.lessonType, t)}</span> : null}
                          {e.teacher ? <span className="pill pill-on">{e.teacher}</span> : null}
                          {e.room ? (
                            <button
                              className="pill pill-off"
                              onClick={() => {
                                if (e.room) openMapWithRoom(e.room);
                              }}
                              style={{ border: "none", background: "inherit", cursor: "pointer" }}
                            >
                              {e.room}
                            </button>
                          ) : (
                            <span className="pill pill-muted">{t("common.noRoom")}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="section">
            <div className="row wrap">
              <button className="btn primary" onClick={() => openExternalUrl("https://lms.astanait.edu.kz/")}> 
                {t("common.openMoodle")}
              </button>
            </div>
          </div>
        </div>

        <div className="hero-card accent-border">
          <div className="hero-title">{t("home.nextLesson")}</div>
          <div className="hero-sub">{nextLesson ? "" : t("home.noNextLesson")}</div>

          {nextLesson ? (
            <div className="home-mini" style={{ marginTop: 10 }}>
              <div className="mini-row">
                <div>
                  <div className="title">{nextLesson.title}</div>
                  <div className="muted small">
                    {formatTime(nextLesson.start)}{nextLesson.lessonType ? ` • ${formatLessonType(nextLesson.lessonType, t)}` : ""} - {nextLesson.room || t("common.noRoom")}
                  </div>
                </div>
                {nextLesson.room ? (
                  <button
                    className="btn ghost"
                    onClick={() => {
                      if (nextLesson.room) openMapWithRoom(nextLesson.room);
                    }}
                  >
                    {t("common.openMap")}
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="empty" style={{ marginTop: 10 }}>
              <div className="empty-title">{t("home.noNextLesson")}</div>
              <div className="muted small">{t("home.noLessonsToday.sub")}</div>
            </div>
          )}

          <div className="section">
            <h3>{t("home.upcomingDeadlines")}</h3>
            {upcomingDeadlines.length === 0 ? (
              <div className="muted small">{t("home.noDeadlines")}</div>
            ) : (
              <div className="home-mini">
                {upcomingDeadlines.map((d) => (
                  <div key={d.uid} className="mini-row">
                    <div style={{ minWidth: 0 }}>
                      <div className="title" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {d.title}
                      </div>
                      <div className="muted small">
                        {new Date(d.due || d.start).toLocaleString()} {d.room ? `- ${d.room}` : ""}
                      </div>
                    </div>
                    {d.moodleUrl ? (
                      <button className="btn ghost" onClick={() => openExternalUrl(d.moodleUrl!)}>
                        {t("common.openInMoodle")}
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
